import { useEffect, useState } from "react";
import axios from "axios";
import { Activity, MessageCircle, ShieldCheck, Receipt, UserPlus, Loader2, Send, FileText, ArrowRight, Pencil, Upload, Trash2, CheckCircle2, XCircle, ShieldAlert, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_META = {
    status: { icon: ArrowRight, color: "text-cyan-700 bg-cyan-50" },
    case_created: { icon: FileText, color: "text-slate-700 bg-slate-100" },
    assign: { icon: UserPlus, color: "text-indigo-700 bg-indigo-50" },
    chat: { icon: MessageCircle, color: "text-emerald-700 bg-emerald-50" },
    invoice: { icon: Receipt, color: "text-amber-700 bg-amber-50" },
    payment: { icon: ShieldCheck, color: "text-emerald-700 bg-emerald-50" },
    edit: { icon: Pencil, color: "text-slate-700 bg-slate-100" },
    design_upload: { icon: Upload, color: "text-violet-700 bg-violet-50" },
    design_delete: { icon: Trash2, color: "text-rose-700 bg-rose-50" },
    design_approve: { icon: CheckCircle2, color: "text-emerald-700 bg-emerald-50" },
    design_reject: { icon: XCircle, color: "text-rose-700 bg-rose-50" },
    design_self_approve: { icon: ShieldAlert, color: "text-amber-700 bg-amber-50" },
    material_missing: { icon: AlertTriangle, color: "text-rose-700 bg-rose-50" },
};

function fmt(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("en-GB", { hour12: false, dateStyle: "medium", timeStyle: "short" }); }
    catch { return String(iso); }
}

/**
 * Salesforce-style chronological feed: status changes, assignments, chat, invoices, payments.
 * Newest first. Pulls /api/cases/:id/activity which combines status_history + chat + invoices.
 */
export default function CaseActivity({ caseId }) {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        axios.get(`${API}/cases/${caseId}/activity`)
            .then(({ data }) => { if (alive) setEvents(data.events || []); })
            .catch(() => { if (alive) setEvents([]); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [caseId]);

    return (
        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="case-activity">
            <div className="flex items-center justify-between mb-4">
                <h3 className="font-display text-[16px] tracking-tight text-slate-900 inline-flex items-center gap-2">
                    <Activity size={15} className="text-cyan-600" /> Activity timeline
                </h3>
                <span className="text-[11.5px] text-slate-500 font-mono-tr">{events.length} event{events.length !== 1 ? "s" : ""}</span>
            </div>
            {loading ? (
                <div className="py-6 flex justify-center"><Loader2 size={18} className="animate-spin text-slate-400" /></div>
            ) : events.length === 0 ? (
                <div className="py-8 text-center text-[13px] text-slate-500">No activity yet.</div>
            ) : (
                <ol className="relative space-y-3 ps-6 border-s-2 border-slate-100">
                    {events.map((ev, i) => {
                        const meta = KIND_META[ev.kind] || KIND_META.status;
                        const Icon = meta.icon || Send;
                        return (
                            <li key={`${ev.kind}-${ev.at}-${i}`} className="relative" data-testid={`activity-${ev.kind}-${i}`}>
                                <div className={`absolute -start-[34px] top-0.5 w-7 h-7 rounded-full grid place-items-center ${meta.color}`}>
                                    <Icon size={13} />
                                </div>
                                <div className="pb-1">
                                    <div className="text-[13.5px] text-slate-900 font-medium leading-snug">{ev.label}</div>
                                    {ev.detail && <div className="text-[12.5px] text-slate-600 mt-0.5">{ev.detail}</div>}
                                    <div className="mt-1 text-[11px] text-slate-500 font-mono-tr tracking-wide">
                                        {fmt(ev.at)} {ev.by && <span className="text-slate-400">· {ev.by}</span>}
                                    </div>
                                </div>
                            </li>
                        );
                    })}
                </ol>
            )}
        </div>
    );
}
